// components/admin/OrderManager.js - Complete with Firebase Storage for Manual Orders
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Eye, Check, Truck, Search, X, RefreshCcw, Package, 
  Clock, Phone, MapPin, UserPlus, ChevronLeft, ChevronRight, 
  Ban, Calendar, CheckCircle, Send, Filter, DollarSign,
  TrendingUp, Wallet, CreditCard, Banknote, Users, ShoppingBag,
  AlertCircle, Copy, MoreVertical, Download, Plus, Minus,
  ChevronDown, Grid, List, Zap, Shield, Star, Award, Flower2,
  RefreshCw, Receipt, ExternalLink, HandCoins, Landmark, FolderTree,
  Folder, ShoppingCart, User as UserIcon, Mail, Phone as PhoneIcon,
  ArrowLeft, Flower, Trash2, Edit, Save, Building, Home, MapPin as MapPinIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import { dbPush, dbUpdate, dbGet, dbOnValue } from '../../config/firebase';

// Custom Dialog Component (keep as is)
const CustomDialog = ({ isOpen, onClose, message, type }) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => onClose(), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch(type) {
      case 'success': return <CheckCircle size={48} color="#10b981" />;
      case 'error': return <AlertCircle size={48} color="#ef4444" />;
      case 'warning': return <AlertCircle size={48} color="#f59e0b" />;
      case 'refund': return <RefreshCw size={48} color="#f59e0b" />;
      default: return <CheckCircle size={48} color="#10b981" />;
    }
  };

  const getBgColor = () => {
    switch(type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'refund': return '#f59e0b';
      default: return '#10b981';
    }
  };

  const getTitle = () => {
    switch(type) {
      case 'success': return 'Success';
      case 'error': return 'Error';
      case 'warning': return 'Warning';
      case 'refund': return 'Refund Processed';
      default: return 'Notice';
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5000,
        backdropFilter: 'blur(4px)', animation: 'fadeIn 0.2s ease'
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: 'white', borderRadius: '16px', zIndex: 5001,
        width: '90%', maxWidth: '400px', padding: '28px', textAlign: 'center',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)', animation: 'scaleIn 0.2s ease'
      }}>
        <div style={{
          width: '72px', height: '72px', borderRadius: '50%', background: `${getBgColor()}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
        }}>
          {getIcon()}
        </div>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '8px', color: '#1e293b' }}>
          {getTitle()}
        </h3>
        <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '20px' }}>{message}</p>
        <div style={{
          width: '60px', height: '3px', background: '#e2e8f0', borderRadius: '3px',
          margin: '0 auto', overflow: 'hidden'
        }}>
          <div style={{
            width: '100%', height: '100%', background: getBgColor(),
            borderRadius: '3px', animation: 'shrink 3s linear forwards'
          }} />
        </div>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
        @keyframes shrink { from { width: 100%; } to { width: 0%; } }
      `}</style>
    </>
  );
};

// Confirm Action Modal (keep as is)
const ConfirmActionModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText }) => {
  if (!isOpen) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200, backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '16px', zIndex: 1201, width: '90%', maxWidth: '400px', padding: '24px' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '30px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <AlertCircle size={28} color="#f59e0b" />
          </div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px', color: '#1e293b' }}>{title}</h3>
          <p style={{ fontSize: '0.85rem', color: '#64748b' }}>{message}</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}>{cancelText || 'Cancel'}</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: '10px', background: '#dc2626', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>{confirmText || 'Confirm'}</button>
        </div>
      </div>
    </>
  );
};

// Time Selector Modal (keep as is)
const TimeSelectorModal = ({ isOpen, onClose, onConfirm, order }) => {
  const [selectedHour, setSelectedHour] = useState('10');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const [selectedPeriod, setSelectedPeriod] = useState('AM');

  const hours = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
  const periods = ['AM', 'PM'];

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(`${selectedHour}:${selectedMinute} ${selectedPeriod}`);
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '20px', zIndex: 1101, width: '90%', maxWidth: '520px', padding: '28px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>Set Delivery Time</h3>
            <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>Select estimated delivery time for customer</p>
          </div>
          <button onClick={onClose} style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#f1f5f9', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ background: '#fefaf8', borderRadius: '12px', padding: '16px', marginBottom: '24px', border: '1px solid rgba(220,38,38,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#fff5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Package size={24} color="#dc2626" />
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Order ID</div>
              <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>#{order?.id?.slice(0, 12)}</div>
              <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '2px' }}>Customer: {order?.customerName}</div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '12px', color: '#475569' }}>Select Time</label>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', background: '#fefaf8', padding: '20px', borderRadius: '16px', border: '1px solid rgba(220,38,38,0.1)' }}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '8px' }}>Hour</div>
              <select value={selectedHour} onChange={(e) => setSelectedHour(e.target.value)} style={{ padding: '12px 8px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '1rem', fontWeight: 500, background: 'white', cursor: 'pointer', width: '100%', textAlign: 'center' }}>
                {hours.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <span style={{ fontSize: '1.5rem', fontWeight: 600, color: '#64748b', marginTop: '-8px' }}>:</span>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '8px' }}>Minute</div>
              <select value={selectedMinute} onChange={(e) => setSelectedMinute(e.target.value)} style={{ padding: '12px 8px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '1rem', fontWeight: 500, background: 'white', cursor: 'pointer', width: '100%', textAlign: 'center' }}>
                {minutes.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '8px' }}>Period</div>
              <select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)} style={{ padding: '12px 8px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '1rem', fontWeight: 500, background: 'white', cursor: 'pointer', width: '100%', textAlign: 'center' }}>
                {periods.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', background: '#f1f5f9', borderRadius: '10px', cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
          <button onClick={handleConfirm} style={{ flex: 1, padding: '12px', background: '#dc2626', color: 'white', borderRadius: '10px', cursor: 'pointer', fontWeight: 600 }}>Confirm Dispatch</button>
        </div>
      </div>
    </>
  );
};

// OTP Modal (keep as is)
const OTPModal = ({ isOpen, onClose, onConfirm }) => {
  const [otp, setOtp] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    onConfirm(otp);
    setOtp('');
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '20px', zIndex: 1101, width: '90%', maxWidth: '400px', padding: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>Verify Delivery OTP</h3>
          <button onClick={onClose} style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <input 
            type="text" 
            maxLength={6} 
            value={otp} 
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} 
            placeholder="Enter 6-digit OTP" 
            style={{ 
              width: '100%', padding: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', 
              fontSize: '1rem', textAlign: 'center', letterSpacing: '4px', fontFamily: 'monospace' 
            }} 
            autoFocus 
          />
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
          <button onClick={handleConfirm} disabled={otp.length !== 6} style={{ flex: 1, padding: '12px', background: otp.length === 6 ? '#dc2626' : '#94a3b8', color: 'white', borderRadius: '8px', cursor: otp.length === 6 ? 'pointer' : 'not-allowed', fontWeight: 600 }}>Verify & Deliver</button>
        </div>
      </div>
    </>
  );
};

// Small Product Card Component
const SmallProductCard = ({ product, cartQuantity, onAddToCart }) => {
  const discountPercent = product.oldPrice && product.oldPrice > product.price 
    ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) : 0;

  return (
    <div style={{
      background: 'white',
      borderRadius: '10px',
      overflow: 'hidden',
      border: '1px solid rgba(220,38,38,0.1)',
      transition: 'all 0.2s ease',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(220,38,38,0.1)';
      e.currentTarget.style.borderColor = '#dc2626';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
      e.currentTarget.style.borderColor = 'rgba(220,38,38,0.1)';
    }}>
      <div style={{ position: 'relative', height: '140px', background: '#fefaf8' }}>
        <img 
          src={product.image} 
          alt={product.name} 
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=200&q=80'}
        />
        {discountPercent > 0 && (
          <div style={{ position: 'absolute', top: '6px', left: '6px', background: '#dc2626', color: 'white', padding: '2px 6px', borderRadius: '3px', fontSize: '9px', fontWeight: 700 }}>
            {discountPercent}% OFF
          </div>
        )}
        {cartQuantity > 0 && (
          <div style={{ position: 'absolute', top: '6px', right: '6px', background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: 700 }}>
            {cartQuantity}
          </div>
        )}
      </div>
      <div style={{ padding: '8px' }}>
        <h4 style={{ fontSize: '11px', fontWeight: 600, marginBottom: '4px', color: '#1a0a0a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {product.name}
        </h4>
        <div style={{ marginBottom: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#dc2626' }}>₹{product.price?.toLocaleString()}</span>
          {product.oldPrice && (
            <span style={{ fontSize: '9px', color: '#94a3b8', textDecoration: 'line-through', marginLeft: '4px' }}>₹{product.oldPrice?.toLocaleString()}</span>
          )}
        </div>
        {cartQuantity > 0 ? (
          <div style={{ display: 'flex', gap: '4px' }}>
            <button 
              onClick={(e) => { e.stopPropagation(); onAddToCart(product, -1); }}
              style={{ flex: 1, padding: '4px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '4px', fontSize: '9px', fontWeight: 600, cursor: 'pointer' }}
            >
              -
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onAddToCart(product, 1); }}
              style={{ flex: 1, padding: '4px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', fontSize: '9px', fontWeight: 600, cursor: 'pointer' }}
            >
              +
            </button>
          </div>
        ) : (
          <button 
            onClick={(e) => { e.stopPropagation(); onAddToCart(product, 1); }}
            style={{ width: '100%', padding: '4px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', fontSize: '9px', fontWeight: 600, cursor: 'pointer' }}
          >
            Add
          </button>
        )}
      </div>
    </div>
  );
};

// Category Card Component
const CategoryCardComp = ({ category, onClick, productCount }) => (
  <div onClick={() => onClick(category)} style={{
    textAlign: 'center',
    cursor: 'pointer',
    background: 'white',
    borderRadius: '12px',
    padding: '12px 8px',
    border: '1px solid rgba(220,38,38,0.1)',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-3px)';
    e.currentTarget.style.boxShadow = '0 6px 16px rgba(220,38,38,0.1)';
    e.currentTarget.style.borderColor = '#dc2626';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
    e.currentTarget.style.borderColor = 'rgba(220,38,38,0.1)';
  }}>
    <span style={{ fontSize: '28px', display: 'block', marginBottom: '6px' }}>{category.icon || '🌸'}</span>
    <h4 style={{ fontSize: '12px', fontWeight: 600, color: '#1a0a0a', marginBottom: '2px' }}>{category.name}</h4>
    <p style={{ fontSize: '10px', color: '#dc2626', fontWeight: 500 }}>{productCount} products</p>
  </div>
);

// Subcategory Card Component
const SubcategoryCardComp = ({ subcategory, onClick, productCount }) => (
  <div onClick={() => onClick(subcategory)} style={{
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    background: 'white',
    borderRadius: '8px',
    padding: '10px 12px',
    border: '1px solid rgba(220,38,38,0.1)',
    transition: 'all 0.2s ease',
    marginBottom: '6px'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateX(3px)';
    e.currentTarget.style.borderColor = '#dc2626';
    e.currentTarget.style.background = '#fff5f5';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateX(0)';
    e.currentTarget.style.borderColor = 'rgba(220,38,38,0.1)';
    e.currentTarget.style.background = 'white';
  }}>
    <Folder size={18} color="#dc2626" />
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 500, fontSize: '13px', color: '#1a0a0a' }}>{subcategory.name}</div>
      <div style={{ fontSize: '10px', color: '#64748b' }}>{productCount} products</div>
    </div>
    <ChevronRight size={14} color="#dc2626" />
  </div>
);

// Cart Sidebar Component
const CartSidebar = ({ cartItems, onUpdateQuantity, onRemove, getCartTotal, getTotalItems, onSubmit, submitting, onClose }) => (
  <div style={{
    position: 'fixed',
    right: 0,
    top: 0,
    bottom: 0,
    width: '350px',
    background: 'white',
    zIndex: 2001,
    boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideIn 0.3s ease'
  }}>
    <div style={{
      padding: '14px 16px',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
      color: 'white'
    }}>
      <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
        <ShoppingCart size={16} /> Cart ({getTotalItems()} items)
      </h3>
      <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer', color: 'white' }}>✕</button>
    </div>

    <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
      {cartItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <ShoppingCart size={40} color="#cbd5e1" />
          <p style={{ marginTop: '10px', fontSize: '12px', color: '#64748b' }}>Cart is empty</p>
        </div>
      ) : (
        <>
          {cartItems.map(item => (
            <div key={item.productId} style={{
              display: 'flex',
              gap: '10px',
              background: '#fefaf8',
              borderRadius: '8px',
              padding: '10px',
              marginBottom: '8px',
              border: '1px solid rgba(220,38,38,0.1)'
            }}>
              <img src={item.image} alt={item.name} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '12px', color: '#1a0a0a' }}>{item.name}</div>
                <div style={{ fontSize: '11px', color: '#dc2626' }}>₹{item.price?.toLocaleString()}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button onClick={() => onUpdateQuantity(item.productId, item.quantity - 1)} style={{ width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}><Minus size={10} /></button>
                    <span style={{ minWidth: '24px', textAlign: 'center', fontSize: '12px', fontWeight: 600 }}>{item.quantity}</span>
                    <button onClick={() => onUpdateQuantity(item.productId, item.quantity + 1)} style={{ width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}><Plus size={10} /></button>
                  </div>
                  <button onClick={() => onRemove(item.productId)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#dc2626' }}>
                ₹{(item.price * item.quantity).toLocaleString()}
              </div>
            </div>
          ))}
          
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontWeight: 600, fontSize: '13px' }}>Total:</span>
              <span style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626' }}>₹{getCartTotal().toLocaleString()}</span>
            </div>
          </div>
        </>
      )}
    </div>

    {cartItems.length > 0 && (
      <div style={{ padding: '12px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <button
          onClick={onSubmit}
          disabled={submitting}
          style={{
            width: '100%',
            padding: '10px',
            background: submitting ? '#94a3b8' : '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: submitting ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            fontSize: '13px'
          }}
        >
          {submitting ? 'Processing...' : <><HandCoins size={14} /> Create Order</>}
        </button>
      </div>
    )}
  </div>
);

// Manual Entry Screen Component - FIXED to store in Firebase
const ManualEntryScreen = ({ categories, subcategories, productsList, onSave, onClose }) => {
  const [step, setStep] = useState('categories');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [city, setCity] = useState('Solapur');
  const [pincode, setPincode] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get filtered products
  const getFilteredProducts = () => {
    let filtered = [...productsList];
    
    if (selectedSubcategory) {
      filtered = filtered.filter(p => 
        p.subcategoryId === selectedSubcategory.id || 
        p.subcategory === selectedSubcategory.name
      );
    } else if (selectedCategory) {
      filtered = filtered.filter(p => 
        p.category === selectedCategory.id || 
        p.mainCategory === selectedCategory.id
      );
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(term) || 
        p.id?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  // Get product counts
  const getCategoryProductCount = (category) => {
    return productsList.filter(p => 
      p.category === category.id || p.mainCategory === category.id
    ).length;
  };

  const getSubcategoryProductCount = (subcategory) => {
    return productsList.filter(p => 
      p.subcategoryId === subcategory.id || p.subcategory === subcategory.name
    ).length;
  };

  // Cart functions
  const addToCart = (product, delta = 1) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.productId === product.id);
      if (existing) {
        const newQuantity = existing.quantity + delta;
        if (newQuantity <= 0) {
          return prev.filter(item => item.productId !== product.id);
        }
        return prev.map(item => 
          item.productId === product.id 
            ? { ...item, quantity: newQuantity }
            : item
        );
      }
      return [...prev, {
        productId: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: 1
      }];
    });
  };

  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      setCartItems(prev => prev.filter(item => item.productId !== productId));
    } else {
      setCartItems(prev => prev.map(item => 
        item.productId === productId 
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.productId !== productId));
  };

  const getCartTotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  const handleReset = () => {
    setStep('categories');
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setCartItems([]);
    setSearchTerm('');
    setCustomerName('');
    setCustomerPhone('');
    setCustomerEmail('');
    setDeliveryAddress('');
    setCity('Solapur');
    setPincode('');
    setPaymentMethod('Cash');
    setShowCart(false);
  };

  const handleSubmit = async () => {
    if (cartItems.length === 0) {
      toast.error('Please add at least one product to cart');
      return;
    }
    if (!customerName) {
      toast.error('Please enter customer name');
      return;
    }
    if (!customerPhone) {
      toast.error('Please enter customer phone');
      return;
    }
    if (!deliveryAddress) {
      toast.error('Please enter delivery address');
      return;
    }

    setSubmitting(true);
    try {
      const orderId = 'MAN' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).substr(2, 4).toUpperCase();
      
      const orderItems = cartItems.map(item => ({
        id: item.productId,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image: item.image,
        variantId: 'standard',
        variantName: 'Standard'
      }));
      
      const orderData = {
        id: orderId,
        transactionId: 'MANUAL_' + Date.now().toString(36).toUpperCase(),
        customerName: customerName,
        customerPhone: customerPhone,
        customerEmail: customerEmail || '',
        items: orderItems,
        totalAmount: getCartTotal(),
        paymentMethod: paymentMethod,
        paymentStatus: 'completed',
        orderStatus: 'confirmed',
        orderDate: new Date().toISOString(),
        isManualPayment: true,
        manualPaymentMethod: paymentMethod,
        deliveryAddress: {
          line1: deliveryAddress,
          city: city || 'Solapur',
          pincode: pincode || '413001'
        },
        statusHistory: [{
          status: 'confirmed',
          timestamp: new Date().toISOString(),
          message: `Manual order created with ${paymentMethod} payment`
        }]
      };

      // Save to Firebase
      const result = await onSave(orderData);
      console.log('Manual order saved to Firebase:', result);
      
      toast.success(`Manual order added successfully! Order ID: ${orderId}`);
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error saving manual order:', error);
      toast.error('Failed to add manual order: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: '#f8fafc', 
      zIndex: 2000,
      overflowY: 'auto'
    }}>
      {/* Cart Sidebar */}
      {showCart && (
        <CartSidebar
          cartItems={cartItems}
          onUpdateQuantity={updateCartQuantity}
          onRemove={removeFromCart}
          getCartTotal={getCartTotal}
          getTotalItems={getTotalItems}
          onSubmit={handleSubmit}
          submitting={submitting}
          onClose={() => setShowCart(false)}
        />
      )}
      {showCart && (
        <div onClick={() => setShowCart(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000 }} />
      )}

      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #dc2626, #b91c1c)', 
        padding: isMobile ? '10px 14px' : '14px 20px',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button 
              onClick={() => {
                if (step === 'subcategories') {
                  setStep('categories');
                  setSelectedCategory(null);
                } else if (step === 'products') {
                  setStep(selectedSubcategory ? 'subcategories' : 'categories');
                  setSelectedSubcategory(null);
                } else {
                  onClose();
                }
              }}
              style={{ 
                background: 'rgba(255,255,255,0.2)', 
                border: 'none', 
                padding: '6px 12px', 
                borderRadius: '6px', 
                cursor: 'pointer', 
                color: 'white', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '5px',
                fontSize: '12px'
              }}
            >
              <ArrowLeft size={14} /> Back
            </button>
            <div>
              <h1 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0, color: 'white' }}>Manual Order Entry</h1>
              <p style={{ fontSize: '0.65rem', opacity: 0.9, marginTop: '2px' }}>
                {step === 'categories' && 'Select a category'}
                {step === 'subcategories' && `Select subcategory in ${selectedCategory?.name}`}
                {step === 'products' && `Add products to cart`}
              </p>
            </div>
          </div>
          
          {/* Cart Button */}
          <button
            onClick={() => setShowCart(true)}
            style={{
              position: 'relative',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              padding: '6px 14px',
              borderRadius: '6px',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px'
            }}
          >
            <ShoppingCart size={16} />
            Cart
            {getTotalItems() > 0 && (
              <span style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                background: '#ff9f00',
                color: '#1a0a0a',
                fontSize: '9px',
                fontWeight: 700,
                minWidth: '16px',
                height: '16px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {getTotalItems()}
              </span>
            )}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '20px 16px' }}>
        {/* Step 1: Categories */}
        {step === 'categories' && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>
                Select a category to browse products
              </p>
            </div>
            {categories.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '12px' }}>
                <Package size={40} color="#cbd5e1" />
                <p style={{ marginTop: '10px', color: '#64748b' }}>Loading categories...</p>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)', 
                gap: '12px' 
              }}>
                {categories.map(cat => (
                  <CategoryCardComp
                    key={cat.id}
                    category={cat}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setStep('subcategories');
                    }}
                    productCount={getCategoryProductCount(cat)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Subcategories */}
        {step === 'subcategories' && selectedCategory && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: 500 }}>
                Subcategories in {selectedCategory.name}
              </p>
            </div>
            {subcategories[selectedCategory.id] && subcategories[selectedCategory.id].length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {subcategories[selectedCategory.id].map(sub => (
                  <SubcategoryCardComp
                    key={sub.id}
                    subcategory={sub}
                    onClick={() => {
                      setSelectedSubcategory(sub);
                      setStep('products');
                    }}
                    productCount={getSubcategoryProductCount(sub)}
                  />
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '12px' }}>
                <Folder size={40} color="#cbd5e1" />
                <p style={{ marginTop: '10px', color: '#64748b' }}>No subcategories found</p>
                <button 
                  onClick={() => setStep('products')} 
                  style={{ marginTop: '12px', padding: '8px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                >
                  View All Products
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Products - Small Grid View */}
        {step === 'products' && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ position: 'relative', marginBottom: '12px' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#dc2626' }} />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                />
              </div>
              <p style={{ color: '#64748b', fontSize: '0.75rem' }}>
                {filteredProducts.length} products found | Cart: {getTotalItems()} items
              </p>
            </div>

            {filteredProducts.length > 0 ? (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', 
                gap: '12px' 
              }}>
                {filteredProducts.map(product => {
                  const cartItem = cartItems.find(item => item.productId === product.id);
                  const cartQuantity = cartItem ? cartItem.quantity : 0;
                  return (
                    <SmallProductCard
                      key={product.id}
                      product={product}
                      cartQuantity={cartQuantity}
                      onAddToCart={addToCart}
                    />
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '12px' }}>
                <Package size={40} color="#cbd5e1" />
                <p style={{ marginTop: '10px', color: '#64748b' }}>No products found</p>
                <button 
                  onClick={() => setStep(selectedSubcategory ? 'subcategories' : 'categories')} 
                  style={{ marginTop: '12px', padding: '8px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                >
                  Go Back
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Customer Details Form - Moved outside cart sidebar for better UX */}
      <div style={{ maxWidth: '500px', margin: '0 auto', padding: '20px' }}>
        {cartItems.length > 0 && (
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginTop: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', color: '#1a0a0a' }}>Customer Details</h3>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '5px', color: '#dc2626' }}>Customer Name *</label>
              <input
                type="text"
                placeholder="Enter customer name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '5px', color: '#dc2626' }}>Phone Number *</label>
              <input
                type="tel"
                placeholder="10-digit mobile number"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                maxLength={10}
                style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '5px', color: '#dc2626' }}>Email (Optional)</label>
              <input
                type="email"
                placeholder="customer@example.com"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '5px', color: '#dc2626' }}>Delivery Address *</label>
              <textarea
                placeholder="House/Flat No., Street, Landmark"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                rows={2}
                style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }}
              />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '5px', color: '#dc2626' }}>Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: 'white' }}
              >
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Card">Card</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button
                onClick={handleReset}
                style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}
              >
                Reset
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !customerName || !customerPhone || !deliveryAddress}
                style={{ 
                  flex: 1, 
                  padding: '10px', 
                  background: (submitting || !customerName || !customerPhone || !deliveryAddress) ? '#94a3b8' : '#dc2626', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: (submitting || !customerName || !customerPhone || !deliveryAddress) ? 'not-allowed' : 'pointer', 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                {submitting ? 'Creating...' : <><HandCoins size={16} /> Create Order</>}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

// Main OrderManager Component
const OrderManager = ({ orders, loading, onConfirmOrder, onProcessOrder, onDispatchOrder, onVerifyOTP, onAssignOrder, onRejectOrder, staffList, products }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [dateFilter, setDateFilter] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [dialog, setDialog] = useState({ isOpen: false, message: '', type: '' });
  const [manualOrders, setManualOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [productsList, setProductsList] = useState([]);
  const itemsPerPage = 10;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load categories and subcategories from Firebase
  useEffect(() => {
    const loadData = async () => {
      try {
        let fbCats = await dbGet('shop_categories');
        let catsArray = [];
        
        if (fbCats && Array.isArray(fbCats)) {
          catsArray = fbCats;
        } else if (fbCats && typeof fbCats === 'object') {
          catsArray = Object.values(fbCats);
        }
        
        if (catsArray.length === 0) {
          catsArray = [
            { id: 'bouquets', name: 'Bouquets', icon: '💐' },
            { id: 'fresh-flowers', name: 'Fresh Flowers', icon: '🌸' },
            { id: 'gift-hampers', name: 'Gift Hampers', icon: '🎁' },
            { id: 'plants', name: 'Plants', icon: '🪴' },
          ];
        }
        setCategories(catsArray);

        let fbSubs = await dbGet('shop_subcategories');
        let subsObj = fbSubs || {};
        setSubcategories(subsObj);
      } catch (err) { 
        console.error('Error loading categories:', err);
      }
    };
    loadData();
  }, []);

  // Load products into flat array
  useEffect(() => {
    if (!products) {
      setProductsList([]);
      return;
    }
    
    let flatProducts = [];
    if (Array.isArray(products)) {
      flatProducts = products;
    } else if (typeof products === 'object') {
      Object.values(products).forEach(category => {
        if (Array.isArray(category)) {
          flatProducts.push(...category);
        }
      });
    }
    
    setProductsList(flatProducts);
  }, [products]);

  // Load manual orders from localStorage and also check Firebase
  useEffect(() => {
    const loadManualOrders = async () => {
      try {
        // Try to load from Firebase first
        const firebaseManualOrders = await dbGet('manual_orders');
        if (firebaseManualOrders && typeof firebaseManualOrders === 'object') {
          const ordersArray = Object.entries(firebaseManualOrders).map(([key, order]) => ({
            ...order,
            firebaseKey: key,
            id: order.id || key
          }));
          setManualOrders(ordersArray);
          localStorage.setItem('manual_orders', JSON.stringify(ordersArray));
        } else {
          // Fallback to localStorage
          const saved = localStorage.getItem('manual_orders');
          if (saved) {
            setManualOrders(JSON.parse(saved));
          }
        }
      } catch (error) {
        console.error('Error loading manual orders:', error);
        const saved = localStorage.getItem('manual_orders');
        if (saved) {
          setManualOrders(JSON.parse(saved));
        }
      }
    };
    loadManualOrders();
  }, []);

  const showDialog = (message, type = 'success') => {
    setDialog({ isOpen: true, message, type });
  };

  // Helper function to filter orders by date range
  const getOrdersByDateRange = (ordersList, startDate, endDate) => {
    return ordersList.filter(o => {
      const orderDate = new Date(o.orderDate);
      return orderDate >= startDate && orderDate < endDate;
    });
  };

  // Get current date range based on date filter
  const getCurrentDateRange = () => {
    if (dateFilter && selectedDate) {
      const startDate = new Date(selectedDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 1);
      return { startDate, endDate, label: new Date(selectedDate).toLocaleDateString('en-IN') };
    } else {
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 1);
      return { startDate, endDate, label: 'Today' };
    }
  };

  const { startDate, endDate } = getCurrentDateRange();

  // In OrderManager.js, replace the stats calculation section with this:

// Combine all orders (regular + manual)
const allOrders = [...(orders || []), ...manualOrders];
const dateFilteredOrders = getOrdersByDateRange(allOrders, startDate, endDate);

// Calculate stats - EXCLUDE cancelled orders from all calculations
// In OrderManager.js, update the stats calculation section:

// Calculate stats - ONLY show amounts for DELIVERED orders
const todayOrders = dateFilteredOrders.filter(o => o.orderStatus !== 'cancelled');
const todayDeliveredOrders = dateFilteredOrders.filter(o => o.orderStatus === 'delivered');
const todayRevenue = todayDeliveredOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);

// COD - Only show delivered COD orders amount
const todayCodOrders = dateFilteredOrders.filter(o => 
  o.paymentMethod === 'Cash on Delivery' && 
  !o.isManualPayment && 
  o.orderStatus !== 'cancelled'
);
const todayDeliveredCodOrders = dateFilteredOrders.filter(o => 
  o.paymentMethod === 'Cash on Delivery' && 
  !o.isManualPayment && 
  o.orderStatus === 'delivered'
);
const todayCodAmount = todayDeliveredCodOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);

// Online - Only show delivered online orders amount
const todayOnlineOrders = dateFilteredOrders.filter(o => 
  o.paymentMethod !== 'Cash on Delivery' && 
  o.paymentStatus === 'completed' && 
  !o.isManualPayment && 
  o.orderStatus !== 'cancelled'
);
const todayDeliveredOnlineOrders = dateFilteredOrders.filter(o => 
  o.paymentMethod !== 'Cash on Delivery' && 
  o.paymentStatus === 'completed' && 
  !o.isManualPayment && 
  o.orderStatus === 'delivered'
);
const todayOnlineAmount = todayDeliveredOnlineOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);

// Manual - Only show delivered manual orders amount
const todayManualOrders = dateFilteredOrders.filter(o => 
  o.isManualPayment === true && 
  o.orderStatus !== 'cancelled'
);
const todayDeliveredManualOrders = dateFilteredOrders.filter(o => 
  o.isManualPayment === true && 
  o.orderStatus === 'delivered'
);
const todayManualAmount = todayDeliveredManualOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);

// Payment received but delivery pending (Online + Manual payments that are not delivered yet)
const paymentReceivedPending = allOrders.filter(o => 
  (o.paymentMethod !== 'Cash on Delivery' || o.isManualPayment === true) && 
  o.orderStatus !== 'delivered' && 
  o.orderStatus !== 'cancelled'
);
const pendingDeliveryAmount = paymentReceivedPending.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
const pendingDeliveryCount = paymentReceivedPending.length;

// Total revenue from delivered orders only
const totalRevenue = allOrders.filter(o => o.orderStatus === 'delivered').reduce((sum, o) => sum + (o.totalAmount || 0), 0);
const totalDeliveredCount = allOrders.filter(o => o.orderStatus === 'delivered').length;
 

  // Update tabs array - cancelled tab shows only cancelled orders
const tabs = [
  { key: 'pending', label: 'Pending', count: allOrders.filter(o => o.orderStatus === 'pending' && !o.isManualPayment && o.orderStatus !== 'cancelled').length || 0 },
  { key: 'confirmed', label: 'Confirmed', count: allOrders.filter(o => o.orderStatus === 'confirmed' && !o.isManualPayment && o.orderStatus !== 'cancelled').length || 0 },
  { key: 'processing', label: 'Processing', count: allOrders.filter(o => o.orderStatus === 'processing' && !o.isManualPayment && o.orderStatus !== 'cancelled').length || 0 },
  { key: 'out', label: 'Out for Delivery', count: allOrders.filter(o => o.orderStatus === 'out' && !o.isManualPayment && o.orderStatus !== 'cancelled').length || 0 },
  { key: 'delivered', label: 'Delivered', count: allOrders.filter(o => o.orderStatus === 'delivered' && !o.isManualPayment).length || 0 },
  { key: 'cancelled', label: 'Cancelled', count: allOrders.filter(o => o.orderStatus === 'cancelled' && !o.isManualPayment).length || 0 },
  { key: 'manual', label: 'Manual Orders', count: manualOrders.filter(o => o.orderStatus !== 'cancelled').length || 0 }
];

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fef3c7', color: '#d97706', label: 'Pending' },
      confirmed: { bg: '#dbeafe', color: '#2563eb', label: 'Confirmed' },
      processing: { bg: '#ede9fe', color: '#7c3aed', label: 'Processing' },
      out: { bg: '#ede9fe', color: '#7c3aed', label: 'Out for Delivery' },
      delivered: { bg: '#d1fae5', color: '#059669', label: 'Delivered' },
      cancelled: { bg: '#fee2e2', color: '#dc2626', label: 'Cancelled' }
    };
    return styles[status] || { bg: '#f1f5f9', color: '#64748b', label: status };
  };

  // Check if order is assigned to staff
  const isOrderAssigned = (order) => {
    return order.assignedTo && order.assignedToId;
  };

  // Get orders based on active tab - SHOW ALL ORDERS (including assigned ones)
  // Get orders based on active tab
const getTabOrders = () => {
  if (activeTab === 'manual') {
    return manualOrders.filter(o => o.orderStatus !== 'cancelled');
  }
  if (activeTab === 'cancelled') {
    return allOrders.filter(order => order.orderStatus === 'cancelled' && !order.isManualPayment);
  }
  // Show ALL orders for admin, excluding cancelled from non-cancelled tabs
  return allOrders.filter(order => 
    order.orderStatus === activeTab && 
    !order.isManualPayment && 
    order.orderStatus !== 'cancelled'
  );
};

  // Search across all fields
  const getFilteredOrders = () => {
    let filtered = getTabOrders();
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order => 
        order.id?.toLowerCase().includes(term) ||
        order.customerName?.toLowerCase().includes(term) ||
        order.customerPhone?.includes(term) ||
        order.customerEmail?.toLowerCase().includes(term) ||
        order.paymentMethod?.toLowerCase().includes(term) ||
        order.totalAmount?.toString().includes(term)
      );
    }
    
    if (dateFilter && selectedDate && activeTab !== 'manual') {
      const filterDate = new Date(selectedDate);
      filterDate.setHours(0, 0, 0, 0);
      const nextDate = new Date(filterDate);
      nextDate.setDate(filterDate.getDate() + 1);
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.orderDate);
        return orderDate >= filterDate && orderDate < nextDate;
      });
    }
    
    return filtered.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
  };

  const filteredOrders = getFilteredOrders();
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (page) => { 
    setCurrentPage(page); 
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  };

  // Admin Actions
  const handleAcceptOrder = async (order) => {
    setConfirmAction({ type: 'accept', order });
    setShowConfirmModal(true);
  };

  const handleRejectOrder = async (order) => {
    setConfirmAction({ type: 'reject', order });
    setShowConfirmModal(true);
  };

  const executeAction = async () => {
    if (!confirmAction) return;
    const { type, order } = confirmAction;
    const identifier = order.firebaseKey || order.id;
    
    if (type === 'accept') {
      const result = await onConfirmOrder(identifier);
      showDialog(result.message, result.success ? 'success' : 'error');
    } else {
      const result = await onRejectOrder(identifier);
      showDialog(result.message, result.success ? 'success' : 'error');
    }
    
    setShowConfirmModal(false);
    setConfirmAction(null);
    setTimeout(() => window.location.reload(), 1500);
  };

  // Admin can process orders
  const handleProcess = async (order) => {
    const identifier = order.firebaseKey || order.id;
    const result = await onProcessOrder(identifier);
    showDialog(result.message, result.success ? 'success' : 'error');
  };

  const handleOpenTimeModal = (order) => {
    setSelectedOrder(order);
    setShowTimeModal(true);
  };

  const handleDispatch = async (timeString) => {
    const identifier = selectedOrder.firebaseKey || selectedOrder.id;
    const result = await onDispatchOrder(identifier, timeString);
    showDialog(result.message, result.success ? 'success' : 'error');
    setShowTimeModal(false);
    setSelectedOrder(null);
  };

  const handleOpenOTPModal = (order) => {
    setSelectedOrder(order);
    setShowOTPModal(true);
  };

  const handleVerifyDelivery = async (otp) => {
    const identifier = selectedOrder.firebaseKey || selectedOrder.id;
    const result = await onVerifyOTP(identifier, otp);
    if (result === true || result?.success) {
      showDialog('Order delivered successfully!', 'success');
      setShowOTPModal(false);
      setSelectedOrder(null);
    } else {
      showDialog(result?.message || 'Invalid OTP. Please try again.', 'error');
    }
  };

  const handleAssignStaff = async (order, staffId) => {
    setAssignLoading(true);
    const identifier = order.firebaseKey || order.id;
    const selectedStaffMember = staffList?.find(s => s.id === staffId);
    
    if (!selectedStaffMember) {
      toast.error('Staff member not found');
      setAssignLoading(false);
      return;
    }
    
    const result = await onAssignOrder(identifier, staffId);
    if (result.success) {
      toast.success(`Order assigned to ${selectedStaffMember.name}`);
      setShowAssignModal(false);
      setSelectedStaff('');
      setSelectedOrder(null);
      setTimeout(() => window.location.reload(), 1000);
    } else {
      toast.error(result.message);
    }
    setAssignLoading(false);
  };

  // Save manual order to Firebase
  const handleAddManualOrder = async (orderData) => {
    try {
      // Save to Firebase 'orders' collection (same as regular orders)
      const result = await dbPush('orders', orderData);
      const newOrder = { ...orderData, firebaseKey: result.key, id: orderData.id };
      
      // Also store in manualOrders state for UI
      setManualOrders(prev => [...prev, newOrder]);
      
      // Update localStorage backup
      const updatedManualOrders = [...manualOrders, newOrder];
      localStorage.setItem('manual_orders', JSON.stringify(updatedManualOrders));
      
      return { success: true, order: newOrder };
    } catch (error) {
      console.error('Error saving manual order to Firebase:', error);
      throw error;
    }
  };

  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const clearDateFilter = () => {
    setDateFilter(false);
    setSelectedDate('');
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60, flexDirection: 'column', gap: 16 }}>
        <RefreshCcw size={40} className="spinner" color="#64748b" />
        <p style={{ color: '#64748b' }}>Loading orders...</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '24px' }}>
      <CustomDialog isOpen={dialog.isOpen} onClose={() => setDialog({ isOpen: false, message: '', type: '' })} message={dialog.message} type={dialog.type} />
      <ConfirmActionModal isOpen={showConfirmModal} onClose={() => { setShowConfirmModal(false); setConfirmAction(null); }} onConfirm={executeAction} title={confirmAction?.type === 'accept' ? 'Accept Order' : 'Reject Order'} message={confirmAction?.type === 'accept' ? 'Are you sure you want to accept this order?' : 'Are you sure you want to reject this order?'} confirmText={confirmAction?.type === 'accept' ? 'Accept' : 'Reject'} cancelText="Cancel" />

      {/* Manual Entry Full Screen */}
      {showManualEntry && (
        <ManualEntryScreen
          categories={categories}
          subcategories={subcategories}
          productsList={productsList}
          onSave={handleAddManualOrder}
          onClose={() => setShowManualEntry(false)}
        />
      )}

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1a0a0a', marginBottom: '4px' }}>Order Management</h1>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Manage and track all customer orders</p>
          </div>
          
          {/* Manual Entry Button */}
          <button
            onClick={() => setShowManualEntry(true)}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 600,
              fontSize: '14px'
            }}
          >
            <HandCoins size={18} /> Manual Order Entry
          </button>
        </div>
      </div>

     {/* Stats Cards */}
<div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
  {/* Today's Orders Card - Shows count of orders (not amount) */}
  <div style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', borderRadius: '16px', padding: '20px', color: 'white', cursor: 'pointer' }} onClick={() => { setDateFilter(true); setSelectedDate(new Date().toISOString().split('T')[0]); setActiveTab('pending'); }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
      <Calendar size={24} style={{ opacity: 0.9 }} />
      <span style={{ fontSize: '0.7rem', background: 'rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: '20px' }}>{dateFilter && selectedDate ? new Date(selectedDate).toLocaleDateString('en-IN') : 'Today'}</span>
    </div>
    
    <div style={{  fontSize: '1.6rem', fontWeight: 700}}>₹{todayRevenue.toLocaleString()} </div>
    <div style={{ fontSize: '0.85rem', fontWeight: 700 ,opacity: 0.7, marginTop: '2px' }}>{todayOrders.length} Order's</div>
    <div style={{ fontSize: '0.85rem', fontWeight: 700, opacity: 0.9, marginTop: '4px' }}>Total revenue</div>
  </div>

  {/* COD Card - Shows DELIVERED COD amount and total COD orders count */}
  <div style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', borderRadius: '16px', padding: '20px', color: 'white' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
      <Banknote size={24} style={{ opacity: 0.9 }} />
      <span style={{ fontSize: '0.7rem', background: 'rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: '20px' }}>COD</span>
    </div>
    <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>₹{todayCodAmount.toLocaleString()}</div>
    <div style={{ fontSize: '0.7rem', opacity: 0.9, marginTop: '4px' }}>{todayCodOrders.length} total orders</div>
    <div style={{ fontSize: '0.6rem', opacity: 0.7, marginTop: '2px' }}>{todayDeliveredCodOrders.length} delivered</div>
  </div>

  {/* Online Card - Shows DELIVERED online amount and total online orders count */}
  <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: '16px', padding: '20px', color: 'white' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
      <CreditCard size={24} style={{ opacity: 0.9 }} />
      <span style={{ fontSize: '0.7rem', background: 'rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: '20px' }}>Online</span>
    </div>
    <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>₹{todayOnlineAmount.toLocaleString()}</div>
    <div style={{ fontSize: '0.7rem', opacity: 0.9, marginTop: '4px' }}>{todayOnlineOrders.length} total orders</div>
    <div style={{ fontSize: '0.6rem', opacity: 0.7, marginTop: '2px' }}>{todayDeliveredOnlineOrders.length} delivered</div>
  </div>

  {/* Manual Card - Shows DELIVERED manual amount and total manual orders count */}
  <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', borderRadius: '16px', padding: '20px', color: 'white', cursor: 'pointer' }} onClick={() => setShowManualEntry(true)}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
      <HandCoins size={24} style={{ opacity: 0.9 }} />
      <span style={{ fontSize: '0.7rem', background: 'rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: '20px' }}>Manual</span>
    </div>
    <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>₹{todayManualAmount.toLocaleString()}</div>
    <div style={{ fontSize: '0.7rem', opacity: 0.9, marginTop: '4px' }}>{todayManualOrders.length} total orders</div>
    <div style={{ fontSize: '0.6rem', opacity: 0.7, marginTop: '2px' }}>{todayDeliveredManualOrders.length} delivered</div>
    <div style={{ fontSize: '0.6rem', opacity: 0.7, marginTop: '4px' }}>Click to add</div>
  </div>

  {/* Pending Delivery Card - Shows amount NOT yet delivered (Online + Manual payments pending delivery) */}
  <div style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', borderRadius: '16px', padding: '20px', color: 'white' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
      <Wallet size={24} style={{ opacity: 0.9 }} />
      <span style={{ fontSize: '0.7rem', background: 'rgba(255,255,255,0.2)', padding: '4px 8px', borderRadius: '20px' }}>Pending Delivery</span>
    </div>
    <div style={{ fontSize: '1.6rem', fontWeight: 700 }}>₹{pendingDeliveryAmount.toLocaleString()}</div>
    <div style={{ fontSize: '0.7rem', opacity: 0.9, marginTop: '4px' }}>{pendingDeliveryCount} orders</div>
    <div style={{ fontSize: '0.6rem', opacity: 0.7, marginTop: '2px' }}>Payment received</div>
  </div>
</div>

      {/* Search Bar */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid rgba(220,38,38,0.1)' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#dc2626' }} />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by Order ID, Customer, Phone, Email, Payment..." style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', outline: 'none' }} />
            {searchTerm && <button onClick={clearSearch} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={14} /></button>}
          </div>
          {activeTab !== 'manual' && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', background: '#fff5f5', padding: '4px 12px', borderRadius: '8px', border: '1px solid rgba(220,38,38,0.2)' }}>
              <Calendar size={16} color="#dc2626" />
              <input type="date" value={selectedDate} onChange={(e) => { setSelectedDate(e.target.value); setDateFilter(true); setCurrentPage(1); }} style={{ padding: '6px', border: 'none', borderRadius: '6px', fontSize: '0.8rem', outline: 'none', background: 'transparent' }} />
              {dateFilter && <button onClick={clearDateFilter} style={{ padding: '4px 8px', background: '#e2e8f0', borderRadius: '6px', fontSize: '0.7rem', cursor: 'pointer' }}>Clear</button>}
            </div>
          )}
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: 'auto' }}>Showing {paginatedOrders.length} of {filteredOrders.length} {activeTab === 'manual' ? 'manual orders' : activeTab + ' orders'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', overflowX: 'auto' }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => { setActiveTab(tab.key); setCurrentPage(1); setSearchTerm(''); if (tab.key !== 'manual') { setDateFilter(false); setSelectedDate(''); } }} style={{ padding: '10px 20px', border: 'none', background: 'transparent', color: activeTab === tab.key ? '#dc2626' : '#64748b', fontSize: '0.875rem', fontWeight: activeTab === tab.key ? 600 : 500, cursor: 'pointer', borderBottom: activeTab === tab.key ? '2px solid #dc2626' : '2px solid transparent', whiteSpace: 'nowrap' }}>
            {tab.label}
            <span style={{ marginLeft: '6px', padding: '2px 6px', borderRadius: '20px', background: activeTab === tab.key ? '#dc2626' : '#e2e8f0', color: activeTab === tab.key ? 'white' : '#64748b', fontSize: '0.7rem' }}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Orders Table */}
      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid rgba(220,38,38,0.1)', overflow: 'hidden' }}>
        {paginatedOrders.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <Package size={48} color="#cbd5e1" style={{ marginBottom: '16px' }} />
            <h3 style={{ fontSize: '1rem', fontWeight: 500, color: '#1e293b', marginBottom: '8px' }}>No {activeTab} orders</h3>
            <p style={{ fontSize: '0.8rem', color: '#64748b' }}>Try adjusting your search or filters</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fff5f5' }}>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>Order ID</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>Customer</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>Payment</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>Assigned</th>
                  <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map(order => {
                  const badge = getStatusBadge(order.orderStatus);
                  const isManualOrder = order.isManualPayment === true;
                  const isAssigned = isOrderAssigned(order);
                  
                  return (
                    <tr key={order.firebaseKey || order.id} style={{ borderBottom: '1px solid #f1f5f9', background: isManualOrder ? '#fefaf8' : (isAssigned ? '#fff8e7' : 'white') }}>
                      <td style={{ padding: '14px 16px', fontSize: '0.85rem' }}>
                        <span style={{ fontWeight: 600, color: '#0f172a', fontFamily: 'monospace' }}>#{order.id?.slice(0, 12)}</span>
                        {isManualOrder && <div style={{ fontSize: '0.6rem', color: '#8b5cf6', marginTop: '2px' }}>Manual Entry</div>}
                        {isAssigned && !isManualOrder && <div style={{ fontSize: '0.6rem', color: '#f59e0b', marginTop: '2px' }}>Assigned to {order.assignedTo}</div>}
                        <div style={{ fontSize: '0.65rem', color: '#64748b' }}>{new Date(order.orderDate).toLocaleDateString()}</div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '0.85rem' }}>
                        <div style={{ fontWeight: 500, color: '#1e293b' }}>{order.customerName}</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {order.customerPhone}
                          <button onClick={() => handleCall(order.customerPhone)} style={{ padding: '4px 8px', background: '#10b981', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><Phone size={12} color="white" /></button>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '1.1rem', fontWeight: 700, color: '#dc2626' }}>₹{order.totalAmount?.toLocaleString()}</td>
                      <td style={{ padding: '14px 16px', fontSize: '0.85rem' }}>
                        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '0.7rem', background: isManualOrder ? '#ede9fe' : (order.paymentMethod === 'Cash on Delivery' ? '#fef3c7' : '#d1fae5'), color: isManualOrder ? '#7c3aed' : (order.paymentMethod === 'Cash on Delivery' ? '#92400e' : '#065f46') }}>
                          {isManualOrder ? order.manualPaymentMethod || 'Manual' : (order.paymentMethod === 'Cash on Delivery' ? 'COD' : 'Online')}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '0.85rem' }}>
                        <span style={{ padding: '6px 12px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 500, background: badge.bg, color: badge.color }}>{badge.label}</span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '0.85rem' }}>
                        {order.assignedTo ? <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '12px', fontSize: '0.7rem', color: '#475569' }}>{order.assignedTo}</span> : <span style={{ color: '#64748b', fontSize: '0.7rem' }}>—</span>}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button onClick={() => { setSelectedOrder(order); setShowDetailModal(true); }} style={{ padding: '6px 12px', borderRadius: '6px', background: '#f1f5f9', border: 'none', fontSize: '0.7rem', cursor: 'pointer' }}>View</button>
                          
                          {activeTab === 'pending' && !isManualOrder && (
                            <><button onClick={() => handleAcceptOrder(order)} style={{ padding: '6px 12px', borderRadius: '6px', background: '#10b981', color: 'white', border: 'none', fontSize: '0.7rem', cursor: 'pointer' }}>Accept</button>
                            <button onClick={() => handleRejectOrder(order)} style={{ padding: '6px 12px', borderRadius: '6px', background: '#ef4444', color: 'white', border: 'none', fontSize: '0.7rem', cursor: 'pointer' }}>Reject</button></>
                          )}
                          
                          {activeTab === 'confirmed' && !isManualOrder && (
                            <>
                              <button onClick={() => { setSelectedOrder(order); setShowAssignModal(true); }} style={{ padding: '6px 12px', borderRadius: '6px', background: '#dc2626', color: 'white', border: 'none', fontSize: '0.7rem', cursor: 'pointer' }}>Assign Staff</button>
                              <button onClick={() => handleProcess(order)} style={{ padding: '6px 12px', borderRadius: '6px', background: '#f59e0b', color: 'white', border: 'none', fontSize: '0.7rem', cursor: 'pointer' }}>Process Order</button>
                            </>
                          )}
                          
                          {activeTab === 'processing' && !isManualOrder && (
                            <button onClick={() => handleOpenTimeModal(order)} style={{ padding: '6px 12px', borderRadius: '6px', background: '#8b5cf6', color: 'white', border: 'none', fontSize: '0.7rem', cursor: 'pointer' }}>Dispatch</button>
                          )}
                          
                          {activeTab === 'out' && !isManualOrder && (
                            <button onClick={() => handleOpenOTPModal(order)} style={{ padding: '6px 12px', borderRadius: '6px', background: '#10b981', color: 'white', border: 'none', fontSize: '0.7rem', cursor: 'pointer' }}>Deliver</button>
                          )}
                          
                          {isManualOrder && (
                            <span style={{ fontSize: '0.65rem', color: '#8b5cf6', padding: '4px 8px', background: '#ede9fe', borderRadius: '4px' }}>Manual Entry</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {totalPages > 1 && (
          <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', background: '#fff5f5' }}>
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}><ChevronLeft size={14} /> Prev</button>
            {[...Array(Math.min(totalPages, 5)).keys()].map(i => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;
              return (<button key={pageNum} onClick={() => handlePageChange(pageNum)} style={{ padding: '6px 12px', borderRadius: '6px', border: currentPage === pageNum ? 'none' : '1px solid #e2e8f0', background: currentPage === pageNum ? '#dc2626' : 'white', color: currentPage === pageNum ? 'white' : '#475569', cursor: 'pointer', fontSize: '0.75rem', fontWeight: currentPage === pageNum ? 600 : 400, minWidth: '36px' }}>{pageNum}</button>);
            })}
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}>Next <ChevronRight size={14} /></button>
          </div>
        )}
      </div>

      {/* Modals */}
      <TimeSelectorModal isOpen={showTimeModal} onClose={() => setShowTimeModal(false)} onConfirm={handleDispatch} order={selectedOrder} />
      <OTPModal isOpen={showOTPModal} onClose={() => setShowOTPModal(false)} onConfirm={handleVerifyDelivery} />

      {showAssignModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-container" style={{ maxWidth: 450 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Assign to Staff</h3><button className="modal-close" onClick={() => setShowAssignModal(false)}>✕</button></div>
            <div className="modal-body">
              <p style={{ marginBottom: 16, fontSize: '0.85rem' }}>Order: <strong>#{selectedOrder.id?.slice(0, 12)}</strong></p>
              <select value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: 20 }}><option value="">Select Staff Member</option>{staffList?.map(s => (<option key={s.id} value={s.id}>{s.name} ({s.email})</option>))}</select>
              <div style={{ display: 'flex', gap: '12px' }}><button onClick={() => setShowAssignModal(false)} style={{ flex: 1, padding: '10px', background: '#f1f5f9', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button><button onClick={() => handleAssignStaff(selectedOrder, selectedStaff)} disabled={assignLoading || !selectedStaff} style={{ flex: 1, padding: '10px', background: assignLoading || !selectedStaff ? '#94a3b8' : '#dc2626', color: 'white', borderRadius: '8px', cursor: assignLoading || !selectedStaff ? 'not-allowed' : 'pointer' }}>{assignLoading ? 'Assigning...' : 'Assign Staff'}</button></div>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && selectedOrder && !isMobile && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-container" style={{ maxWidth: 650 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><div><h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Order #{selectedOrder.id?.slice(0, 12)}</h3><div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}><span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 500, background: getStatusBadge(selectedOrder.orderStatus).bg, color: getStatusBadge(selectedOrder.orderStatus).color }}>{getStatusBadge(selectedOrder.orderStatus).label}</span><p style={{ fontSize: '0.7rem', color: '#64748b' }}>{new Date(selectedOrder.orderDate).toLocaleString()}</p></div></div><button className="modal-close" onClick={() => setShowDetailModal(false)}>✕</button></div>
            <div className="modal-body">
              {selectedOrder.deliveryTime && (<div style={{ background: '#fefaf8', padding: '12px', borderRadius: '8px', marginBottom: '16px', borderLeft: '3px solid #dc2626' }}><Clock size={14} color="#dc2626" style={{ display: 'inline', marginRight: '8px' }} /><strong>Est. Delivery:</strong> {selectedOrder.deliveryTime}</div>)}
              <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}><h4 style={{ marginBottom: '12px', fontSize: '0.9rem', fontWeight: 600 }}>Customer Details</h4><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}><div><label style={{ fontSize: '0.7rem', color: '#64748b' }}>Name</label><div style={{ fontWeight: 500 }}>{selectedOrder.customerName}</div></div><div><label style={{ fontSize: '0.7rem', color: '#64748b' }}>Phone</label><div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>{selectedOrder.customerPhone}<button onClick={() => handleCall(selectedOrder.customerPhone)} style={{ padding: '4px 8px', background: '#10b981', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><Phone size={12} color="white" /></button></div></div><div><label style={{ fontSize: '0.7rem', color: '#64748b' }}>Email</label><div>{selectedOrder.customerEmail || 'N/A'}</div></div><div><label style={{ fontSize: '0.7rem', color: '#64748b' }}>Payment</label><div>{selectedOrder.paymentMethod}{selectedOrder.isManualPayment && <span style={{ marginLeft: '4px', fontSize: '0.6rem', color: '#8b5cf6' }}>(Manual - {selectedOrder.manualPaymentMethod})</span>}</div></div></div><div style={{ marginTop: '12px' }}><label style={{ fontSize: '0.7rem', color: '#64748b' }}>Address</label><div>{selectedOrder.deliveryAddress?.line1}, {selectedOrder.deliveryAddress?.city} - {selectedOrder.deliveryAddress?.pincode || 'N/A'}</div></div></div>
              <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}><h4 style={{ marginBottom: '12px', fontSize: '0.9rem', fontWeight: 600 }}>Order Items ({selectedOrder.items?.length || 0})</h4><div style={{ maxHeight: '280px', overflowY: 'auto', paddingRight: '8px' }}>{selectedOrder.items?.map((item, i) => (<div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: i !== selectedOrder.items.length - 1 ? '1px solid #e2e8f0' : 'none' }}><div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><img src={item.image} alt="" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' }} /><div><div style={{ fontWeight: 500 }}>{item.name}</div><div style={{ fontSize: '0.7rem', color: '#64748b' }}>Qty: {item.quantity}</div><div style={{ fontSize: '0.7rem', fontWeight: 500, color: '#dc2626' }}>₹{item.price.toLocaleString()} each</div></div></div><div style={{ textAlign: 'right' }}><div style={{ fontWeight: 600 }}>₹{(item.price * item.quantity).toLocaleString()}</div></div></div>))}</div><div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', marginTop: '8px', borderTop: '1px solid #e2e8f0', fontWeight: 600, fontSize: '1rem' }}><span>Total</span><span style={{ color: '#dc2626' }}>₹{selectedOrder.totalAmount?.toLocaleString()}</span></div></div>
              {selectedOrder.assignedTo && (<div style={{ background: '#fff5f5', borderRadius: '8px', padding: '12px' }}><div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><UserPlus size={16} color="#dc2626" /><div><div style={{ fontSize: '0.7rem', color: '#64748b' }}>Assigned To</div><div style={{ fontWeight: 500 }}>{selectedOrder.assignedTo}</div></div></div></div>)}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .spinner { animation: spin 0.6s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(2px); }
        .modal-container { background: white; border-radius: 12px; width: 90%; max-width: 650px; max-height: 85vh; overflow: auto; animation: modalFadeIn 0.2s ease; }
        .modal-header { padding: 16px 20px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; background: white; border-radius: 12px 12px 0 0; }
        .modal-close { width: 32px; height: 32px; border-radius: 8px; background: #f1f5f9; border: none; cursor: pointer; display: flex; alignItems: center; justifyContent: center; }
        .modal-body { padding: 20px; }
        @keyframes modalFadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
};

export default OrderManager;